import * as Sentry from "@sentry/react";

/**
 * コンテキスト情報を定義する型。ページとフィーチャーの組み合わせ、またはフックのいずれかを指定する。
 * @typedef {Object} SafeWarningContext
 * @property {string} [page] - ページ名。`xxxPage`または`xxxApp`の形式。
 * @property {string} [feature] - 機能名。`xxxForm`、`xxxButton`、または`xxxMutation`の形式。
 * @property {string} [hook] - フック名。`usexxx`の形式。
 */
type SafeWarningContext =
  | {
      page: `${string}Page` | `${string}App`;
      feature: `${string}Form` | `${string}Button` | `${string}Mutation`;
    }
  | {
      component: `${string}`;
    }
  | {
      hook: `use${string}`;
    };

/**
 * 警告メッセージをSentryに安全に送信する関数。
 * コンテキスト情報をタグとして付与して送信する。
 * エラーが発生した場合はコンソールにログを出力する。
 *
 * @param {string} message - Sentryに送信する警告メッセージ
 * @param {SafeWarningContext} context - 警告に関連するコンテキスト情報
 * @returns {void} - 戻り値なし
 *
 * @example
 * // ページとフィーチャーのコンテキストを使用する例
 * safeWarning("お薬情報の共有に失敗した", {
 *   page: "MedicineNotebookEkusuLinkPage",
 *   feature: "PatientOnetimeCodeMutation",
 * });
 *
 * @example
 * // フックのコンテキストを使用する例
 * safeWarning("マイナ在宅受付WEBからのコールバック処理に失敗", {
 *   hook: "useAnalyzeMynaHomeWeb"
 * });
 */
export const safeWarning = (message: string, context: SafeWarningContext): void => {
  try {
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      if ("page" in context) {
        scope.setTag("page", context.page);
      }
      if ("feature" in context) {
        scope.setTag("feature", context.feature);
      }
      if ("hook" in context) {
        scope.setTag("hook", context.hook);
      }
      console.warn(message);
      Sentry.captureMessage(message);
    });
  } catch (e) {
    console.error(e);
  }
};
